import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { RootState } from '../redux/store';
import { API_BASE_URL } from '../helpers/common';
import { IESDocument } from '../interfaces/IESDocument';

export interface ISearchParams {
  q: string;
  page?: number;
  limit?: number;
  userRole: string;
  index?:
    | 'clients'
    | 'customers'
    | 'products'
    | 'stock-owners'
    | 'warehouses'
    | 'stock-items'
    | 'orders'
    | 'inventory-logs'
    | 'pallets';
  indexes?: (
    | 'clients'
    | 'customers'
    | 'products'
    | 'stock-owners'
    | 'warehouses'
    | 'stock-items'
    | 'orders'
    | 'inventory-logs'
    | 'pallets'
  )[];
  stockOwnerId?: number;
  ranking?: 'code';
  strategy?: 'es' | 'db';
}

export interface ISearchResult extends IESDocument {
  id: number;
  code?: string;
  sku?: string;
  score?: number;
  companyId?: number;
  _index?: string;
  _type?: string;
  [key: string]: any;
}

export interface ISearchResponse {
  list: ISearchResult[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

export const searchApi = createApi({
  reducerPath: 'searchApi',
  tagTypes: ['Search'],
  refetchOnMountOrArgChange: true,
  baseQuery: fetchBaseQuery({
    baseUrl: API_BASE_URL,
    prepareHeaders: async (headers, { getState }) => {
      const token = (getState() as RootState).user.info.accessToken;
      token && headers.set('Authorization', `Bearer ${token}`);
      return headers;
    },
  }),
  endpoints: (build) => ({
    search: build.query<ISearchResponse, ISearchParams>({
      transformResponse: (response: { data: ISearchResponse }) => response.data,
      query: ({
        q,
        page,
        limit,
        userRole,
        index,
        indexes,
        stockOwnerId,
        ranking,
        strategy,
      }) => {
        const params: any = {
          page,
          limit,
          stockOwnerId,
          ranking,
          strategy,
        };

        // Use indexes if provided, otherwise fall back to single index
        if (indexes && indexes.length > 0) {
          params.indexes = indexes.join(',');
        } else if (index) {
          params.index = index;
        }

        return {
          url: `/${userRole}/search/${encodeURIComponent(q)}`,
          method: 'GET',
          params,
        };
      },
      providesTags: ['Search'],
    }),
  }),
});

export const { useSearchQuery, useLazySearchQuery } = searchApi;
